import { supabase } from './supabase';
import { Tables } from '../types/database.types';

export type Profile = Tables<'profiles'>;
export type UserRole = 'admin' | 'moderator' | 'user';

export interface ProfileWithEmail extends Profile {
  email?: string;
  vote_count?: number;
  post_count?: number;
  comment_count?: number;
}

export interface UserStats {
  total_votes: number;
  total_posts: number;
  total_comments: number;
  points_by_reason: {
    reason: string;
    total_points: number;
  }[];
}

class UsersAdminService {
  /**
   * Get all users with their email from auth.users
   */
  async getAll(): Promise<ProfileWithEmail[]> {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    if (!profiles || profiles.length === 0) {
      return [];
    }

    // Get all user IDs
    const userIds = profiles.map(p => p.id);

    // Get vote counts in a single query
    const { data: voteCounts } = await supabase
      .from('votes')
      .select('user_id')
      .in('user_id', userIds);

    // Get post counts in a single query
    const { data: postCounts } = await supabase
      .from('posts')
      .select('user_id')
      .in('user_id', userIds);

    // Get comment counts in a single query
    const { data: commentCounts } = await supabase
      .from('comments')
      .select('user_id')
      .in('user_id', userIds);

    // Create count maps for fast lookup
    const voteCountMap = new Map<string, number>();
    const postCountMap = new Map<string, number>();
    const commentCountMap = new Map<string, number>();

    (voteCounts || []).forEach(vote => {
      voteCountMap.set(vote.user_id, (voteCountMap.get(vote.user_id) || 0) + 1);
    });

    (postCounts || []).forEach(post => {
      postCountMap.set(post.user_id, (postCountMap.get(post.user_id) || 0) + 1);
    });

    (commentCounts || []).forEach(comment => {
      commentCountMap.set(comment.user_id, (commentCountMap.get(comment.user_id) || 0) + 1);
    });

    // Combine the data
    const profilesWithEmail: ProfileWithEmail[] = profiles.map(profile => ({
      ...profile,
      email: undefined, // Email removed for security (auth.admin not available in frontend)
      vote_count: voteCountMap.get(profile.id) || 0,
      post_count: postCountMap.get(profile.id) || 0,
      comment_count: commentCountMap.get(profile.id) || 0,
    }));

    return profilesWithEmail;
  }

  /**
   * Get user by ID with details
   */
  async getById(id: string): Promise<ProfileWithEmail | null> {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    // Get statistics
    const { count: voteCount } = await supabase
      .from('votes')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', id);

    const { count: postCount } = await supabase
      .from('posts')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', id);

    const { count: commentCount } = await supabase
      .from('comments')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', id);

    return {
      ...profile,
      email: undefined, // Email removed for security (auth.admin not available in frontend)
      vote_count: voteCount || 0,
      post_count: postCount || 0,
      comment_count: commentCount || 0,
    };
  }

  /**
   * Update user profile and role
   */
  async update(
    id: string,
    data: Partial<{
      display_name: string;
      username: string;
      bio: string;
      avatar_url: string;
      role: UserRole;
    }>
  ): Promise<Profile> {
    const { data: profile, error } = await supabase
      .from('profiles')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return profile;
  }

  /**
   * Suspend user
   */
  async suspend(id: string): Promise<Profile> {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        is_suspended: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Unsuspend user
   */
  async unsuspend(id: string): Promise<Profile> {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        is_suspended: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Adjust user points manually
   */
  async adjustPoints(
    userId: string,
    points: number,
    description?: string
  ): Promise<void> {
    // Get current points
    const { data: profile } = await supabase
      .from('profiles')
      .select('total_points')
      .eq('id', userId)
      .single();

    const currentPoints = profile?.total_points || 0;
    const newPoints = currentPoints + points;

    // Update total points
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        total_points: newPoints,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (profileError) throw profileError;

    // Add to points history
    const { error: historyError } = await supabase
      .from('user_points_history')
      .insert({
        user_id: userId,
        points: points,
        reason: 'admin_adjustment',
        description: description || 'Ajuste manual por administrador',
      });

    if (historyError) throw historyError;
  }

  /**
   * Get user statistics
   */
  async getStats(userId: string): Promise<UserStats> {
    // Get vote count
    const { count: totalVotes } = await supabase
      .from('votes')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    // Get post count
    const { count: totalPosts } = await supabase
      .from('posts')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    // Get comment count
    const { count: totalComments } = await supabase
      .from('comments')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    // Get points by reason
    const { data: pointsHistory } = await supabase
      .from('user_points_history')
      .select('reason, points')
      .eq('user_id', userId);

    const pointsByReason = (pointsHistory || []).reduce((acc: any[], item: any) => {
      const existing = acc.find((r) => r.reason === item.reason);
      if (existing) {
        existing.total_points += item.points;
      } else {
        acc.push({
          reason: item.reason,
          total_points: item.points,
        });
      }
      return acc;
    }, []);

    return {
      total_votes: totalVotes || 0,
      total_posts: totalPosts || 0,
      total_comments: totalComments || 0,
      points_by_reason: pointsByReason,
    };
  }

  /**
   * Search users by name, username or email
   */
  async search(query: string): Promise<ProfileWithEmail[]> {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('*')
      .or(`display_name.ilike.%${query}%,username.ilike.%${query}%`)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Add statistics
    const profilesWithEmail: ProfileWithEmail[] = await Promise.all(
      (profiles || []).map(async (profile) => {
        const { count: voteCount } = await supabase
          .from('votes')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', profile.id);

        const { count: postCount } = await supabase
          .from('posts')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', profile.id);

        const { count: commentCount } = await supabase
          .from('comments')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', profile.id);

        return {
          ...profile,
          email: undefined, // Email removed for security (auth.admin not available in frontend)
          vote_count: voteCount || 0,
          post_count: postCount || 0,
          comment_count: commentCount || 0,
        };
      })
    );

    return profilesWithEmail;
  }
}

export const usersAdminService = new UsersAdminService();
